import type { Texture } from 'three/webgpu';
import type { WorldData, WorldMaterialAsset } from '../../world/schema';
import {
  makeSurface,
  type SurfaceMaterial,
  type SurfaceParams,
  type SurfaceTextures,
} from './surfaceFactory';

/**
 * World material catalog (rendering plan §1.2): resolves `assets[]` entries of kind 'material'
 * — archetype + parameter set, pure DATA — into live factory materials, memoized by id per
 * world. Texture references stay UNRESOLVED in R1 (there is no texture loading pipeline yet):
 * the `resolveTexture` seam defaults to undefined, so the factory gets no textures and three's
 * built-in 1×1 default texture covers the slots. The A4 cell loader plugs the real resolver in.
 *
 * Unknown/missing ids warn once and return a shared fallback material (constitution rule 6:
 * warn, never throw — bad world data must not brick the runtime).
 */

/**
 * Texture-asset id → live texture.
 *
 * ⭐ M3 TAKES THE SLOT TOO, AND IT IS NOT OPTIONAL INFORMATION. Colour space is a property of how
 * a texture is USED, not of the file: `map`/`emissiveMap` are authored colour and decode from
 * sRGB, `normalMap`/`orm` are vectors and measurements and must stay linear. The resolver cannot
 * decide correctly without knowing which slot it is answering for — and getting it wrong on an ORM
 * map reads as a lighting problem, so it gets "fixed" in the wrong place.
 *
 * `render/materials/worldTextures.ts` is the implementation this seam was written for.
 */
export type TextureResolver = (
  textureAssetId: string,
  slot: 'map' | 'normalMap' | 'orm' | 'emissiveMap' | 'mask',
) => Texture | undefined;

const perWorld = new WeakMap<WorldData, Map<string, SurfaceMaterial>>();
const warned = new Set<string>();
let fallback: SurfaceMaterial | null = null;

/** Shared loud-magenta fallback — visible, never fatal. */
function fallbackMaterial(): SurfaceMaterial {
  fallback ??= makeSurface('surf.opaque', { color: 0xff00ff, roughness: 1 });
  return fallback;
}

function buildMaterial(asset: WorldMaterialAsset, resolveTexture?: TextureResolver): SurfaceMaterial {
  const p = asset.params ?? {};
  const params: SurfaceParams = { ...p };
  if (asset.textures && resolveTexture) {
    const textures: SurfaceTextures = {};
    for (const slot of ['map', 'normalMap', 'orm', 'emissiveMap'] as const) {
      const id = asset.textures[slot];
      if (id === undefined) continue;
      const tex = resolveTexture(id, slot);
      if (tex) textures[slot] = tex;
    }
    params.textures = textures;
  }
  const mat = makeSurface(asset.archetype, params);
  mat.name = asset.id;
  return mat;
}

/**
 * The material for `world`'s asset `id`. Memoized: the same (world, id) pair always returns the
 * SAME material object — instances share programs by construction (factory) and uniforms stay
 * per-material. Unknown id, or an id of a non-material asset, warns once and returns the
 * fallback.
 */
export function materialFor(
  world: WorldData,
  id: string,
  resolveTexture?: TextureResolver,
): SurfaceMaterial {
  let cache = perWorld.get(world);
  if (!cache) {
    cache = new Map();
    perWorld.set(world, cache);
  }
  const hit = cache.get(id);
  if (hit) return hit;

  const asset = world.assets.find((a) => a.id === id);
  if (!asset || asset.kind !== 'material') {
    const key = `${world.worldId}:${id}`;
    if (!warned.has(key)) {
      warned.add(key);
      console.warn(
        `[materials] world '${world.worldId}' has no material asset '${id}' — using fallback`,
      );
    }
    return fallbackMaterial();
  }

  const mat = buildMaterial(asset, resolveTexture);
  cache.set(id, mat);
  return mat;
}
